import { getUserInsights, convertGeneratedInsightToMarkdown } from '@/insights/full-page-insights/utils/data.utils';
import { useStarsStore } from '@/stores/stars.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useHistoryStore } from '@/stores/history.store';
import { getAllFocusGroups, deleteInsightsSegmentById, getInsightsSegmentAppIds } from '@/insights/utils/utils';
import { getDefaultSegments } from '@/utils/segments';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { LOADING_STATES } from '@/constants';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';
import groupBy from 'lodash/groupBy';
import Vue from 'vue';

function getItemByType (itemType, itemId, rootGetters) {
    if (itemType === 'Page') {
        return rootGetters['pages/pageById'](itemId);
    }
    if (itemType === 'Feature') {
        return rootGetters['features/featureById'](itemId);
    }
    if (itemType === 'TrackType') {
        return rootGetters['trackTypes/trackTypeById'](itemId);
    }

    return null;
}

const { RESOLVED, REJECTED, LOADING, EMPTY } = LOADING_STATES;
const insights = {
    namespaced: true,
    state: {
        focusGroupsMap: {},
        fetchingFocusGroups: false,
        insights: [],
        insightsFetched: false,
        dailyInsights: {
            isFetching: false,
            data: null,
            hasError: false,
        },
    },
    mutations: {
        setFocusGroups (state, { focusGroups }) {
            state.focusGroupsMap = focusGroups;
        },
        setFetchingFocusGroups (state, isFetching) {
            state.fetchingFocusGroups = isFetching;
        },
        setInsights (state, { insights }) {
            state.insights = insights;
            state.insightsFetched = true;
        },
        removeInsightsSegment (state, { id }) {
            Vue.delete(state.focusGroupsMap, id);
        },
        setDailyInsightsFetching (state, isFetching) {
            state.dailyInsights.isFetching = isFetching;
        },
        setDailyInsightsData (state, data) {
            state.dailyInsights.data = data;
        },
        setDailyInsightsError (state, hasError) {
            state.dailyInsights.hasError = hasError;
        },
    },
    actions: {
        async loadAllFocusGroups ({ commit, state, rootGetters }, { reFetch = false } = {}) {
            if (!rootGetters['subscription/hasInsights'] || (!isEmpty(state.focusGroupsMap) && !reFetch)) {
                return Object.values(state.focusGroupsMap);
            }
            try {
                commit('setFetchingFocusGroups', true);
                const focusGroups = await getAllFocusGroups();

                focusGroups.unshift({ id: '0', isFocusGroup: true, name: 'Everyone' });

                const insightsSegmentToAppIdsMap = await getInsightsSegmentAppIds();
                const insightsSegmentsWithAppIds = focusGroups.map((insightsSegment) => {
                    return {
                        ...insightsSegment,
                        appIds: insightsSegmentToAppIdsMap[insightsSegment.id] || [],
                    };
                });

                commit('setFocusGroups', { focusGroups: keyBy(insightsSegmentsWithAppIds, 'id') });
                commit('setFetchingFocusGroups', false);

                return insightsSegmentsWithAppIds;
            } catch (ex) {
                throw new Error(`Failed to load Insights segments: ${ex}`);
            }
        },
        async deleteInsightsSegment ({ commit }, { segmentId, appId }) {
            try {
                await deleteInsightsSegmentById(segmentId, appId);
                commit('removeInsightsSegment', { id: segmentId });
            } catch {
                throw new Error(`Failed to delete Insights segment: ${segmentId}`);
            }
        },
        async getDailyInsights ({ commit, state }, body) {
            if (state.dailyInsights.isFetching) {
                return;
            }

            // Clear existing data to force new fetch when context changes
            if (state.dailyInsights.data?.length > 0) {
                commit('setDailyInsightsData', null);
            }
            commit('setDailyInsightsFetching', true);
            try {
                const resp = await getUserInsights(body);
                const { summaries } = resp.body;
                summaries?.forEach((summary) => {
                    summary.generatedInsights?.forEach((insight) => {
                        insight.markdown = convertGeneratedInsightToMarkdown(insight);
                    });
                });
                commit('setDailyInsightsData', summaries);
                commit('setDailyInsightsError', false);
                commit('setDailyInsightsFetching', false);
            } catch (error) {
                commit('setDailyInsightsError', true);
                commit('setDailyInsightsFetching', false);
                throw new Error(`Failed to fetch daily insights: ${error}`);
            }
        },
    },
    getters: {
        getFocusGroups: (state) => state.focusGroupsMap,
        getFocusGroupIds: (state) => Object.keys(state.focusGroupsMap),
        getFocusGroupName: (state) => (id) => (id === '0' ? 'Everyone' : state.focusGroupsMap[id]?.name || 'Unknown'),
        getFocusGroupById: (state) => (id) => state.focusGroupsMap[id],
        getDailyInsightStatus: (state) => {
            if (state.dailyInsights.hasError) {
                return REJECTED;
            }
            if (state.dailyInsights.data?.length > 0) {
                return RESOLVED;
            }
            if (state.dailyInsights.data?.length === 0) {
                return EMPTY;
            }

            return LOADING;
        },
        getDailyInsightsContext: (state, getters, rootState, rootGetters) => {
            const userInsightsContext = useUserSettingsStore().settingByName('insights-context', true);
            if (userInsightsContext) {
                const { value } = userInsightsContext;
                if (value) {
                    const { events, segment } = value;
                    const keys = Object.keys(events);
                    if (keys.length) {
                        const undeletedEvents = keys.map((key) => {
                            const eventsArray = events[key].filter(
                                (event) => getItemByType(event.kind, event.id, rootGetters) !== undefined,
                            );
                            if (eventsArray.length) {
                                return eventsArray;
                            }

                            return [];
                        });

                        // Check if we have any valid events after filtering
                        const hasValidEvents = undeletedEvents.some((eventArray) => eventArray.length > 0);

                        if (hasValidEvents) {
                            return {
                                events: groupBy(
                                    undeletedEvents.flatMap((events) => events),
                                    'kind',
                                ),
                                segment,
                            };
                        }
                    }
                }
            }

            // Default fallback context
            const defaultSegments = getDefaultSegments();
            let defaultSegment;
            if (hasSubscriptionFlag('showAnonymous')) {
                defaultSegment = defaultSegments.everyoneExcludingAnonymous;
            } else {
                defaultSegment = defaultSegments.everyone;
            }
            const selectedContextEventsByType = { ...getters.getSelectedContextEventsByType };
            const events = {};
            for (const key in selectedContextEventsByType) {
                if (Object.prototype.hasOwnProperty.call(selectedContextEventsByType, key)) {
                    events[key.toLowerCase()] = selectedContextEventsByType[key];
                }
            }

            return {
                events,
                segment: [defaultSegment.id],
            };
        },
        getDefaultInsightsSegment: () => {
            const defaultSegments = getDefaultSegments();
            let defaultSegment;
            if (hasSubscriptionFlag('showAnonymous')) {
                defaultSegment = defaultSegments.everyoneExcludingAnonymous;
            } else {
                defaultSegment = defaultSegments.everyone;
            }

            return defaultSegment;
        },
        getDefaultContextEvents: (state, getters, rootState, rootGetters) => {
            try {
                const defaultSelectedApplication = rootGetters['apps/allWebPlusIntegratedMobileApps']?.[0] || null;
                const { id } = defaultSelectedApplication;
                if (!id || useStarsStore().fetching) {
                    return {};
                }
                const coreEventPages = rootGetters['coreEvents/coreEventListByKindAndApp']('Page', parseInt(id));
                const coreEventFeatures = rootGetters['coreEvents/coreEventListByKindAndApp']('Feature', parseInt(id));
                const coreEventTrackTypes = rootGetters['coreEvents/coreEventListByKindAndApp'](
                    'TrackType',
                    parseInt(id),
                );
                const historyItems = useHistoryStore().allItemHistory([
                    'featureHistory',
                    'trackTypeHistory',
                    'pageHistory',
                ]);
                const starPages = useStarsStore().getStarsByKind('Page');
                const starFeatures = useStarsStore().getStarsByKind('Feature');
                const starTrackTypes = useStarsStore().getStarsByKind('TrackType');

                const selectedContext = [
                    ...starTrackTypes,
                    ...starFeatures,
                    ...starPages,
                    ...historyItems,
                    ...coreEventFeatures,
                    ...coreEventTrackTypes,
                    ...coreEventPages,
                ].map((item) => {
                    return {
                        ...item,
                        id: item.id || item.itemId,
                        kind: item.kind || item.type || item.itemType,
                    };
                });
                const uniqueSelectedContext = uniqBy(selectedContext, 'id');

                let attempts = 0;
                const maxAttempts = uniqueSelectedContext.length; // Prevent infinite loops
                const contextItem = [];

                while (contextItem.length < 3 && attempts < maxAttempts) {
                    const item = uniqueSelectedContext.shift();
                    attempts++;

                    const type = item?.type || item?.kind || item?.itemType;
                    const id = item?.id || item?.itemId;

                    if (type && id) {
                        const itemDetails = getItemByType(type, id, rootGetters);
                        if (itemDetails) {
                            contextItem.push({
                                id,
                                kind: type,
                                appId: itemDetails.appId,
                                name: itemDetails.name,
                            });
                        }
                    }
                }

                return contextItem;
            } catch (error) {
                return error;
            }
        },
        getSelectedContextEventsByType: (state, getters) => {
            const defaultContext = getters.getDefaultContextEvents;
            if (!defaultContext) return {};

            return groupBy(defaultContext, 'kind');
        },
        getSelectedContextEventsByAppId: (state, getters) => {
            if (!getters.getDailyInsightsContext?.events) return {};
            const contextEvents = [];
            Object.keys(getters.getDailyInsightsContext?.events).forEach((key) => {
                const value = getters.getDailyInsightsContext?.events[key];
                contextEvents.push(...value);
            });
            if (!contextEvents) return {};

            return groupBy(contextEvents, 'appId');
        },
    },
};

export default insights;
